import { QueryDocumentSnapshot, serverTimestamp, SnapshotOptions } from "firebase/firestore";
import { auth } from "../services/firebase";

export type ServiceType =
    | "sunday-morning"
    | "sunday-afternoon"
    | "saturday"
    | "wednesday"
    | "weekday-special"
    | "manual";

export type SlotMode = "sermon" | "intro" | "reminder";

export default class SundayService {
    date: Date;
    
    // Choir songs
    choirSong1Name: string;
    choirSong1Number: number;
    choirSong2Name: string;
    choirSong2Number: number;
    choirSong3Name: string;
    choirSong3Number: number;
    choirSong4Name: string;
    choirSong4Number: number;
    
    // First sermon
    sermon1PreacherKey: string;
    sermon1BiblePassage: string;
    sermon1Topic: string;
    sermon1Mode: SlotMode;
    sermon1ConfirmedAt?: Date;
    
    // Second sermon
    sermon2PreacherKey: string;
    sermon2BiblePassage: string;
    sermon2Topic: string;
    sermon2Mode: SlotMode;
    sermon2ConfirmedAt?: Date;

    serviceType: ServiceType;
    serviceLabel: string;
    weekday: string;

    // Prayer concerns – wednesday, saturday, weekday-special use a single concern;
    // sunday-afternoon in prayer mode uses the list.
    prayerConcern?: string;
    prayerConcerns?: string[];

    // sunday-afternoon: controls whether the service is a sermon or prayer-concern service.
    afternoonMode?: "sermon" | "prayer";

    // Imported from the Excel Predigtdienstplan (see functions/src/excelParser.ts).
    verkuendiger: string; // Wochenschluss: Verkündiger, e.g. "L. Schur (LS)"
    guestPreacherOrigin: string; // guest preacher's home Gemeinde, e.g. "Dingerdissen"
    afternoonEvent: string; // Nachmittagsveranstaltung, e.g. "Daniel Pankratz (3-SN)"
    choirInfo: string; // choir note, e.g. "unser Chor bei uns"
    serviceNote: string; // free-text note, e.g. an away-marker

    createdBy: string;
    createdAt: Date;
    changedBy: string;
    changedAt: Date;

    // Slot status set by the preacher self-service (declined / swap requested).
    // Read-only here: written by Cloud Functions, kept by the merging save, and
    // only valid for the preacher named in ...StatusPreacherKey (see utils/slotStatus.ts).
    sermon1Status?: string;
    sermon1StatusPreacherKey?: string;
    sermon1DeclineReason?: string;
    sermon2Status?: string;
    sermon2StatusPreacherKey?: string;
    sermon2DeclineReason?: string;

    constructor(
        date: Date,
        choirSong1Name: string,
        choirSong1Number: number,
        choirSong2Name: string,
        choirSong2Number: number,
        choirSong3Name: string,
        choirSong3Number: number,
        choirSong4Name: string,
        choirSong4Number: number,
        sermon1PreacherKey: string,
        sermon1BiblePassage: string,
        sermon1Topic: string,
        sermon1Mode: SlotMode,
        sermon2PreacherKey: string,
        sermon2BiblePassage: string,
        sermon2Topic: string,
        sermon2Mode: SlotMode,
        createdBy: string,
        createdAt: Date,
        changedBy: string,
        changedAt: Date,
        serviceType: ServiceType = "manual",
        serviceLabel: string = "",
        weekday: string = "",
        sermon1ConfirmedAt?: Date,
        sermon2ConfirmedAt?: Date,
        prayerConcern: string = "",
        prayerConcerns: string[] = [],
        afternoonMode?: "sermon" | "prayer",
        verkuendiger: string = "",
        guestPreacherOrigin: string = "",
        afternoonEvent: string = "",
        choirInfo: string = "",
        serviceNote: string = ""
    ) {
        this.date = date;
        this.choirSong1Name = choirSong1Name;
        this.choirSong1Number = choirSong1Number;
        this.choirSong2Name = choirSong2Name;
        this.choirSong2Number = choirSong2Number;
        this.choirSong3Name = choirSong3Name;
        this.choirSong3Number = choirSong3Number;
        this.choirSong4Name = choirSong4Name;
        this.choirSong4Number = choirSong4Number;
        this.sermon1PreacherKey = sermon1PreacherKey;
        this.sermon1BiblePassage = sermon1BiblePassage;
        this.sermon1Topic = sermon1Topic;
        this.sermon1Mode = sermon1Mode;
        this.sermon1ConfirmedAt = sermon1ConfirmedAt;
        this.sermon2PreacherKey = sermon2PreacherKey;
        this.sermon2BiblePassage = sermon2BiblePassage;
        this.sermon2Topic = sermon2Topic;
        this.sermon2Mode = sermon2Mode;
        this.sermon2ConfirmedAt = sermon2ConfirmedAt;
        this.serviceType = serviceType;
        this.serviceLabel = serviceLabel;
        this.weekday = weekday;
        this.prayerConcern = prayerConcern;
        this.prayerConcerns = prayerConcerns;
        this.afternoonMode = afternoonMode;
        this.verkuendiger = verkuendiger;
        this.guestPreacherOrigin = guestPreacherOrigin;
        this.afternoonEvent = afternoonEvent;
        this.choirInfo = choirInfo;
        this.serviceNote = serviceNote;
        this.createdBy = createdBy;
        this.createdAt = createdAt;
        this.changedBy = changedBy;
        this.changedAt = changedAt;
    }
}

/** A Firestore Timestamp (or Date) as Date; undefined for anything else. */
export function timestampToDate(value: unknown): Date | undefined {
    if (value instanceof Date) return value;
    if (value && typeof (value as { toDate?: unknown }).toDate === "function") {
        const date = (value as { toDate: () => Date }).toDate();
        return date instanceof Date && !isNaN(date.getTime()) ? date : undefined;
    }
    return undefined;
}

export const sundayServiceConverter = {
    toFirestore: (service: SundayService) => {
        return {
            date: service.date,
            choirSong1Name: service.choirSong1Name,
            choirSong1Number: service.choirSong1Number,
            choirSong2Name: service.choirSong2Name,
            choirSong2Number: service.choirSong2Number,
            choirSong3Name: service.choirSong3Name,
            choirSong3Number: service.choirSong3Number,
            choirSong4Name: service.choirSong4Name,
            choirSong4Number: service.choirSong4Number,
            sermon1PreacherKey: service.sermon1PreacherKey,
            sermon1BiblePassage: service.sermon1BiblePassage,
            sermon1Topic: service.sermon1Topic,
            sermon1Mode: service.sermon1Mode,
            ...(service.sermon1ConfirmedAt ? { sermon1ConfirmedAt: service.sermon1ConfirmedAt } : {}),
            sermon2PreacherKey: service.sermon2PreacherKey,
            sermon2BiblePassage: service.sermon2BiblePassage,
            sermon2Topic: service.sermon2Topic,
            sermon2Mode: service.sermon2Mode,
            ...(service.sermon2ConfirmedAt ? { sermon2ConfirmedAt: service.sermon2ConfirmedAt } : {}),
            serviceType: service.serviceType,
            serviceLabel: service.serviceLabel,
            weekday: service.weekday,
            prayerConcern: service.prayerConcern ?? "",
            prayerConcerns: service.prayerConcerns ?? [],
            ...(service.afternoonMode ? { afternoonMode: service.afternoonMode } : {}),
            verkuendiger: service.verkuendiger ?? "",
            guestPreacherOrigin: service.guestPreacherOrigin ?? "",
            afternoonEvent: service.afternoonEvent ?? "",
            choirInfo: service.choirInfo ?? "",
            serviceNote: service.serviceNote ?? "",
            createdBy: service.createdBy,
            createdAt: service.createdAt,
            changedBy: auth.currentUser!.uid,
            changedAt: serverTimestamp(),
        };
    },

    fromFirestore: (snapshot: QueryDocumentSnapshot, options: SnapshotOptions) => {
        const data = snapshot.data(options);
        const date = timestampToDate(data.date);
        if (!date) throw new Error(`Gottesdienst ${snapshot.id} hat kein Datum`);
        const service = new SundayService(
            date,
            data.choirSong1Name,
            data.choirSong1Number,
            data.choirSong2Name,
            data.choirSong2Number,
            data.choirSong3Name,
            data.choirSong3Number,
            data.choirSong4Name,
            data.choirSong4Number,
            data.sermon1PreacherKey,
            data.sermon1BiblePassage,
            data.sermon1Topic,
            data.sermon1Mode ?? "sermon",
            data.sermon2PreacherKey,
            data.sermon2BiblePassage,
            data.sermon2Topic,
            data.sermon2Mode ?? "sermon",
            data.createdBy,
            // Documents written by scripts may lack the timestamps (GOD-59): fall back to the service date.
            timestampToDate(data.createdAt) ?? date,
            data.changedBy,
            timestampToDate(data.changedAt) ?? timestampToDate(data.createdAt) ?? date,
            data.serviceType ?? "manual",
            data.serviceLabel ?? "",
            data.weekday ?? "",
            data.sermon1ConfirmedAt?.toDate?.(),
            data.sermon2ConfirmedAt?.toDate?.(),
            data.prayerConcern ?? "",
            data.prayerConcerns ?? [],
            data.afternoonMode,
            data.verkuendiger ?? "",
            data.guestPreacherOrigin ?? "",
            data.afternoonEvent ?? "",
            data.choirInfo ?? "",
            data.serviceNote ?? ""
        );
        service.sermon1Status = data.sermon1Status;
        service.sermon1StatusPreacherKey = data.sermon1StatusPreacherKey;
        service.sermon1DeclineReason = data.sermon1DeclineReason;
        service.sermon2Status = data.sermon2Status;
        service.sermon2StatusPreacherKey = data.sermon2StatusPreacherKey;
        service.sermon2DeclineReason = data.sermon2DeclineReason;
        return service;
    },
};
